import React, { useEffect, useRef } from "react";

/**
 * Ambient dot field.
 *
 * A grid of small colorful dots in the EMIBOOK palette that drift continuously
 * on their own, and push away from the cursor when it comes near.
 *
 * Motion model:
 *   1. Every dot traces a slow elliptical path of its own (independent X/Y
 *      frequencies), so the whole field is always visibly alive — you can see
 *      it moving without touching the mouse.
 *   2. The pointer is interpolated toward the real cursor, and each dot eases
 *      toward a repulsion offset. Both use plain lerps with no velocity term,
 *      so the field reacts promptly but can never overshoot or jitter.
 *   3. The loop pauses entirely when the section scrolls out of view.
 */

// Cursor response — higher is snappier. Visible, but still damped.
const POINTER_EASE = 0.16;
const DOT_EASE = 0.18;

const INFLUENCE_RADIUS = 180;
const MAX_PUSH = 52;

// Ambient drift — each dot's own slow float.
const DRIFT_AMP_MIN = 5;
const DRIFT_AMP_MAX = 13;
// Radians per millisecond. ~3s–7s per cycle.
const DRIFT_SPEED_MIN = 0.0009;
const DRIFT_SPEED_MAX = 0.0021;

const SPACING_DESKTOP = 34;
const SPACING_MOBILE = 46;

const TINTS = [
  [37, 99, 235],   // EMIBOOK blue
  [79, 70, 229],   // indigo
  [124, 58, 237],  // soft purple
  [8, 145, 178],   // teal
  [5, 150, 105],   // emerald
];

// Dots are batched by colour string so the whole field costs a handful of
// fill() calls per frame instead of one per dot.
const ALPHA_BUCKETS = 4;

export default function DotField({ className = "", intensity = 1, spacingScale = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Cursor interaction is desktop-only; a touch device still gets the drift.
    const isFinePointer =
      window.matchMedia && window.matchMedia("(pointer: fine)").matches;

    const ctx = canvas.getContext("2d");
    const host = canvas.parentElement;

    let dots = [];
    let groups = [];
    let width = 0;
    let height = 0;
    let rafId = null;
    let running = false;
    let inView = true;
    // Drift clock. Accumulated across pauses so scrolling away and back
    // resumes the motion where it left off instead of jumping phase.
    let elapsed = 0;
    let segmentStart = 0;

    // Real cursor target, and the lagging interpolated pointer that drives physics.
    let targetX = -9999;
    let targetY = -9999;
    let pointerX = -9999;
    let pointerY = -9999;

    const build = () => {
      const rect = host.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      if (width === 0 || height === 0) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const spacing = (width < 768 ? SPACING_MOBILE : SPACING_DESKTOP) * spacingScale;
      const cols = Math.ceil(width / spacing) + 1;
      const rows = Math.ceil(height / spacing) + 1;

      dots = [];
      const byColor = new Map();

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          // Deterministic pseudo-random so the field is stable across resizes.
          const seed = (r * 73856093) ^ (c * 19349663);
          const a = Math.abs(seed);
          const rand = (n) => ((a >> n) % 1000) / 1000;

          const jx = (rand(0) - 0.5) * spacing * 0.55;
          const jy = (rand(5) - 0.5) * spacing * 0.55;

          const tintIdx = a % TINTS.length;
          const depth = rand(3); // 0..1

          const alpha = (0.22 + depth * 0.26) * intensity;
          const bucket = Math.min(
            ALPHA_BUCKETS - 1,
            Math.floor((alpha / (0.48 * Math.max(intensity, 0.001))) * ALPHA_BUCKETS)
          );
          const [tr, tg, tb] = TINTS[tintIdx];
          const bucketAlpha = (
            (0.22 + ((bucket + 0.5) / ALPHA_BUCKETS) * 0.26) *
            intensity
          ).toFixed(3);
          const color = `rgba(${tr}, ${tg}, ${tb}, ${bucketAlpha})`;

          const dot = {
            hx: c * spacing + jx,
            hy: r * spacing + jy,
            ox: 0,
            oy: 0,
            radius: 1.1 + depth * 1,               // 1.1 – 2.1px
            // Lighter dots drift further — reads as depth, not chaos.
            reach: 0.6 + depth * 0.6,
            ampX: DRIFT_AMP_MIN + rand(7) * (DRIFT_AMP_MAX - DRIFT_AMP_MIN),
            ampY: DRIFT_AMP_MIN + rand(11) * (DRIFT_AMP_MAX - DRIFT_AMP_MIN),
            spdX: DRIFT_SPEED_MIN + rand(13) * (DRIFT_SPEED_MAX - DRIFT_SPEED_MIN),
            spdY: DRIFT_SPEED_MIN + rand(17) * (DRIFT_SPEED_MAX - DRIFT_SPEED_MIN),
            phX: rand(19) * Math.PI * 2,
            phY: rand(23) * Math.PI * 2,
          };

          dots.push(dot);
          if (!byColor.has(color)) byColor.set(color, []);
          byColor.get(color).push(dot);
        }
      }

      groups = [...byColor.entries()].map(([color, members]) => ({ color, members }));
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, width, height);

      for (let g = 0; g < groups.length; g++) {
        const { color, members } = groups[g];
        ctx.fillStyle = color;
        ctx.beginPath();
        for (let i = 0; i < members.length; i++) {
          const d = members[i];
          const dx = Math.cos(t * d.spdX + d.phX) * d.ampX;
          const dy = Math.sin(t * d.spdY + d.phY) * d.ampY;
          const x = d.hx + dx + d.ox;
          const y = d.hy + dy + d.oy;
          ctx.moveTo(x + d.radius, y);
          ctx.arc(x, y, d.radius, 0, Math.PI * 2);
        }
        ctx.fill();
      }
    };

    const step = (now) => {
      const t = elapsed + (now - segmentStart);

      // 1. Pointer lags slightly behind the real cursor.
      pointerX += (targetX - pointerX) * POINTER_EASE;
      pointerY += (targetY - pointerY) * POINTER_EASE;

      // 2. Resolve each dot's repulsion offset, then ease toward it.
      const r2 = INFLUENCE_RADIUS * INFLUENCE_RADIUS;
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const dx = d.hx - pointerX;
        const dy = d.hy - pointerY;
        const distSq = dx * dx + dy * dy;

        let tx = 0;
        let ty = 0;
        if (distSq < r2 && distSq > 0.0001) {
          const dist = Math.sqrt(distSq);
          const n = 1 - dist / INFLUENCE_RADIUS;
          // Smoothstep falloff — no hard edge at the influence boundary.
          const falloff = n * n * (3 - 2 * n);
          const push = falloff * MAX_PUSH * d.reach;
          tx = (dx / dist) * push;
          ty = (dy / dist) * push;
        }

        d.ox += (tx - d.ox) * DOT_EASE;
        d.oy += (ty - d.oy) * DOT_EASE;
      }

      draw(t);
      rafId = requestAnimationFrame(step);
    };

    const start = () => {
      if (running || reduceMotion || dots.length === 0) return;
      running = true;
      segmentStart = performance.now();
      rafId = requestAnimationFrame(step);
    };

    const stop = () => {
      if (running) elapsed += performance.now() - segmentStart;
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    };

    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      targetX = e.clientX - rect.left;
      targetY = e.clientY - rect.top;
    };

    const onPointerLeave = () => {
      targetX = -9999;
      targetY = -9999;
    };

    build();
    draw(0);

    if (reduceMotion) {
      // Static field only — no drift, no cursor reaction.
      return () => {};
    }

    if (isFinePointer) {
      window.addEventListener("pointermove", onPointerMove, { passive: true });
      document.addEventListener("pointerleave", onPointerLeave);
      window.addEventListener("blur", onPointerLeave);
    }

    // Only animate while the section is actually on screen.
    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              inView = entries[0].isIntersecting;
              if (inView) start();
              else stop();
            },
            { rootMargin: "120px" }
          )
        : null;
    if (io) io.observe(host);
    else start();

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (inView) start();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        build();
        if (!running) draw(0);
      }, 150);
    };
    window.addEventListener("resize", onResize);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onResize)
        : null;
    if (ro) ro.observe(host);

    return () => {
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("blur", onPointerLeave);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      if (io) io.disconnect();
      if (ro) ro.disconnect();
    };
  }, [intensity, spacingScale]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`eb-dot-field ${className}`}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}
